import { useState } from "react";
import { Facebook, Mail, Link2, Check, Share2 } from "lucide-react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// target: { streamId } for live, or { ownerId, videoId } for a library video.
const shareUrl = (target, src) => {
  const base = target.videoId
    ? `${BACKEND}/api/share/video/${target.ownerId}/${target.videoId}`
    : `${BACKEND}/api/share/live/${target.streamId}`;
  return `${base}?src=${src}`;
};

const trackClick = (target, network) => {
  api.post("/analytics/share-click", {
    network, stream_id: target.videoId ? null : target.streamId, video_id: target.videoId || null,
    session_id: localStorage.getItem("sw_viewer_sid") || null,
  }).catch(() => {});
};

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* iframe may block clipboard API */ }
  const ta = document.createElement("textarea");
  ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
  document.body.appendChild(ta); ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  return ok;
};

// Share row driven by the owner's Player profile switches (settings = {allow, facebook, twitter, email}).
export function ShareButtons({ settings, target, title, dark = true, className = "" }) {
  const [copied, setCopied] = useState(false);
  if (!settings?.allow || !target || !(target.streamId || target.videoId)) return null;
  const t = encodeURIComponent(title || "Watch with us");
  const enc = (src) => encodeURIComponent(shareUrl(target, src));
  const btn = "h-8 w-8 rounded-full flex items-center justify-center transition-[background-color,transform] hover:scale-105 " +
    (dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-700 hover:bg-gray-200");
  const popup = (e, href, network) => {
    trackClick(target, network);
    const w = window.open(href, "sw_share", "width=620,height=560");
    if (w) { w.opener = null; e.preventDefault(); } // else fall back to the normal new-tab link
  };
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${enc("facebook")}`;
  const tw = `https://twitter.com/intent/tweet?url=${enc("x")}&text=${t}`;
  const mail = `mailto:?subject=${t}&body=${encodeURIComponent(`${title || "Watch with us"}\n\n${shareUrl(target, "email")}`)}`;

  return (
    <div data-testid="share-buttons" className={"flex items-center gap-1.5 " + className}>
      <Share2 className={"h-3.5 w-3.5 mr-0.5 " + (dark ? "text-white/60" : "text-gray-400")} />
      {settings.facebook && (
        <a data-testid="share-facebook" href={fb} target="_blank" rel="noopener noreferrer" onClick={(e) => popup(e, fb, "facebook")} title="Share on Facebook" className={btn}><Facebook className="h-4 w-4" /></a>
      )}
      {settings.twitter && (
        <a data-testid="share-twitter" href={tw} target="_blank" rel="noopener noreferrer" onClick={(e) => popup(e, tw, "x")} title="Share on X" className={btn}><XLogo className="h-3.5 w-3.5" /></a>
      )}
      {settings.email && (
        <a data-testid="share-email" href={mail} target="_top" onClick={() => trackClick(target, "email")} title="Share by email" className={btn}><Mail className="h-4 w-4" /></a>
      )}
      <button data-testid="share-copy-link" type="button" title="Copy link" className={btn}
        onClick={async () => { trackClick(target, "link"); if (await copyText(shareUrl(target, "link"))) { setCopied(true); setTimeout(() => setCopied(false), 1800); } }}>
        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Link2 className="h-4 w-4" />}
      </button>
      {copied && <span data-testid="share-copied" className={"text-[11px] " + (dark ? "text-white/70" : "text-gray-500")}>Copied</span>}
    </div>
  );
}
